import Layout from "@/components/Layout";
import PermissionDenied from "@/components/PermissionDenied";
import { CatalogEntityCard, formatCatalogAuditDate } from "@/components/catalog/CatalogEntityCard";
import { GridPaginationFooter, GridPerPageSelect } from "@/components/catalog/GridPaginationControls";
import { ConfirmAlertDialog } from "@/components/ConfirmAlertDialog";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import {
  createWarehouse,
  deleteWarehouse,
  listWarehouseRecords,
  syncWarehousesFromApi,
  updateWarehouse,
  type WarehouseRecord,
} from "@/lib/catalog/warehousesStore";
import { countProductsUsingWarehouse } from "@/lib/catalog/productsStore";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { cn } from "@/lib/utils";
import { isAxiosError } from "axios";
import { useGridPagination } from "@/hooks/useGridPagination";
import { endOfDay, format, isValid, parseISO, startOfDay, startOfMonth, subDays } from "date-fns";
import { CalendarIcon, Edit, Eye, Filter, LayoutGrid, List, Loader2, MapPin, Package, Plus, Search, Trash2 } from "lucide-react";
import { motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "@/components/ui/sonner";

const PAGE_SIZE_OPTIONS = [20, 24, 28, 32, 40, 48, 60] as const;

function locationLine(w: WarehouseRecord): string {
  const parts = [w.city, w.state, w.country].map((s) => s.trim()).filter(Boolean);
  return parts.length ? parts.join(", ") : "—";
}

type WhSortBy = "latest" | "oldest" | "name_asc" | "name_desc" | "city_asc" | "city_desc";

const Warehouses = () => {
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canViewAny = can(user, "warehouse_can_view_any");
  const canViewAsCatalogPeer = can(user, "catalog_attribute_can_view_any");
  const canOpenPage = canViewAny || canViewAsCatalogPeer;
  const canCreate = can(user, "warehouse_can_create");
  const canUpdate = can(user, "warehouse_can_update");
  const canDelete = can(user, "warehouse_can_delete");
  const [isForbidden, setIsForbidden] = useState(false);

  const [version, setVersion] = useState(0);
  const refresh = useCallback(() => setVersion((v) => v + 1), []);

  const [query, setQuery] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sortBy, setSortBy] = useState<WhSortBy>("latest");
  const [filterCreatedDate, setFilterCreatedDate] = useState<Date | undefined>(undefined);
  const [quickDate, setQuickDate] = useState<"all" | "today" | "week" | "month">("all");
  const [filterName, setFilterName] = useState("");
  const [filterCity, setFilterCity] = useState("");
  const [filterCountry, setFilterCountry] = useState("");
  const [filterState, setFilterState] = useState("");
  const [onlyActive, setOnlyActive] = useState(false);
  const [onlyInactive, setOnlyInactive] = useState(false);
  const [minProducts, setMinProducts] = useState("");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<WarehouseRecord | null>(null);
  const [name, setName] = useState("");
  const [country, setCountry] = useState("");
  const [state, setState] = useState("");
  const [city, setCity] = useState("");
  const [address, setAddress] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState<WarehouseRecord | null>(null);
  const [viewRow, setViewRow] = useState<WarehouseRecord | null>(null);
  const [infoDialog, setInfoDialog] = useState<{ title: string; description: string } | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const rows = useMemo(() => listWarehouseRecords(), [version]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "auth_user") setUser(getStoredAuthUser());
    };
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("storage", onStorage);
    window.addEventListener("auth_user_updated", onAuth);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth_user_updated", onAuth);
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setIsForbidden(false);
      try {
        await syncWarehousesFromApi();
        if (mounted) refresh();
      } catch (error) {
        if (mounted && isAxiosError(error) && error.response?.status === 403) {
          setIsForbidden(true);
          return;
        }
        console.error("[Warehouses] Load failed", error);
        if (mounted) toast.error("Could not fetch latest locations. Showing cached data.");
      }
    };
    void load();
    return () => {
      mounted = false;
    };
  }, [refresh]);

  const usageById = useMemo(() => {
    const m = new Map<string, number>();
    for (const w of rows) {
      m.set(w.id, countProductsUsingWarehouse(w.id));
    }
    return m;
  }, [rows]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const nameNeedle = filterName.trim().toLowerCase();
    const cityNeedle = filterCity.trim().toLowerCase();
    const countryNeedle = filterCountry.trim().toLowerCase();
    const stateNeedle = filterState.trim().toLowerCase();
    const minP = Number(minProducts);
    const hasMinP = minProducts.trim() !== "" && Number.isFinite(minP);

    return rows.filter((w) => {
      if (filterCreatedDate) {
        const want = format(filterCreatedDate, "yyyy-MM-dd");
        const created = parseISO(w.createdAt);
        const got = isValid(created) ? format(created, "yyyy-MM-dd") : null;
        if (got !== want) return false;
      }
      if (quickDate !== "all") {
        const created = parseISO(w.createdAt);
        if (!isValid(created)) return false;
        const now = new Date();
        const end = endOfDay(now);
        const start =
          quickDate === "today"
            ? startOfDay(now)
            : quickDate === "week"
              ? startOfDay(subDays(now, 6))
              : startOfMonth(now);
        if (created < start || created > end) return false;
      }
      const matchesQuery =
        !q ||
        w.name.toLowerCase().includes(q) ||
        w.city.toLowerCase().includes(q) ||
        w.state.toLowerCase().includes(q) ||
        w.country.toLowerCase().includes(q) ||
        w.address.toLowerCase().includes(q);
      const matchesName = !nameNeedle || w.name.toLowerCase().includes(nameNeedle);
      const matchesCity = !cityNeedle || w.city.toLowerCase().includes(cityNeedle);
      const matchesCountry = !countryNeedle || w.country.toLowerCase().includes(countryNeedle);
      const matchesState = !stateNeedle || w.state.toLowerCase().includes(stateNeedle);
      const matchesActive = (!onlyActive || w.isActive) && (!onlyInactive || !w.isActive);
      const usage = usageById.get(w.id) ?? 0;
      const matchesProducts = !hasMinP || usage >= minP;
      return matchesQuery && matchesName && matchesCity && matchesCountry && matchesState && matchesActive && matchesProducts;
    });
  }, [
    rows,
    query,
    filterCreatedDate,
    quickDate,
    filterName,
    filterCity,
    filterCountry,
    filterState,
    onlyActive,
    onlyInactive,
    minProducts,
    usageById,
  ]);

  const sorted = useMemo(() => {
    const items = [...filtered];
    items.sort((a, b) => {
      switch (sortBy) {
        case "oldest":
          return a.createdAt.localeCompare(b.createdAt);
        case "name_desc":
          return b.name.localeCompare(a.name);
        case "name_asc":
          return a.name.localeCompare(b.name);
        case "city_desc":
          return b.city.localeCompare(a.city);
        case "city_asc":
          return a.city.localeCompare(b.city);
        case "latest":
        default:
          return b.createdAt.localeCompare(a.createdAt);
      }
    });
    return items;
  }, [filtered, sortBy]);

  const appliedFiltersCount =
    (filterCreatedDate ? 1 : 0) +
    (filterName.trim() ? 1 : 0) +
    (filterCity.trim() ? 1 : 0) +
    (filterCountry.trim() ? 1 : 0) +
    (filterState.trim() ? 1 : 0) +
    (onlyActive ? 1 : 0) +
    (onlyInactive ? 1 : 0) +
    (minProducts.trim() ? 1 : 0);

  const resetSheetFilters = () => {
    setFilterCreatedDate(undefined);
    setFilterName("");
    setFilterCity("");
    setFilterCountry("");
    setFilterState("");
    setOnlyActive(false);
    setOnlyInactive(false);
    setMinProducts("");
  };

  const paginationKey = `${query}|${sortBy}|${quickDate}|${filterCreatedDate?.getTime() ?? ""}|${filterName}|${filterCity}|${filterCountry}|${filterState}|${onlyActive ? "1" : "0"}|${onlyInactive ? "1" : "0"}|${minProducts}`;

  const {
    page,
    setPage,
    pageSize,
    setPageSize,
    totalCount,
    totalPages,
    paginatedItems: paginated,
    rangeStart,
    rangeEnd,
  } = useGridPagination(sorted, paginationKey, {
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    defaultPageSize: PAGE_SIZE_OPTIONS[0],
  });

  const startCreate = () => {
    setEditing(null);
    setName("");
    setCountry("");
    setState("");
    setCity("");
    setAddress("");
    setIsActive(true);
    setOpen(true);
  };

  const startEdit = (w: WarehouseRecord) => {
    setEditing(w);
    setName(w.name);
    setCountry(w.country);
    setState(w.state);
    setCity(w.city);
    setAddress(w.address);
    setIsActive(w.isActive);
    setOpen(true);
  };

  const save = async () => {
    if (isSaving) return;
    if (!name.trim()) {
      toast.error("Location name is required.");
      return;
    }
    setIsSaving(true);
    try {
      if (editing) {
        const res = await updateWarehouse(editing.id, {
          name,
          country,
          state,
          city,
          address,
          isActive,
        });
        toast.success(res?.message || "Location updated.");
      } else {
        const res = await createWarehouse({
          name,
          country,
          state,
          city,
          address,
          isActive,
        });
        toast.success(res.message);
      }
      setOpen(false);
      refresh();
    } catch (error) {
      console.error("[Warehouses] Save failed", error);
      toast.error("Unable to save location. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const requestRemove = (w: WarehouseRecord) => {
    const n = countProductsUsingWarehouse(w.id);
    if (n > 0) {
      setInfoDialog({
        title: "Cannot delete location",
        description: `${n} product(s) reference “${w.name}”. Clear the location on those products first.`,
      });
      return;
    }
    setDeleteTarget(w);
  };

  const confirmRemove = async () => {
    if (!deleteTarget || isDeleting) return;
    setIsDeleting(true);
    try {
      const result = await deleteWarehouse(deleteTarget.id);
      setDeleteTarget(null);
      refresh();
      toast.success(result.message);
    } catch (error) {
      console.error("[Warehouses] Delete failed", error);
      toast.error("Unable to delete location. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  if (!canOpenPage) {
    return (
      <PermissionDenied
        title="Locations access required"
        message="You don’t have permission to open this page. Ask an administrator to grant warehouse_can_view_any or catalog_attribute_can_view_any."
      />
    );
  }

  return isForbidden ? (
    <PermissionDenied
      title="Locations access required"
      message="You don’t have permission to load locations from the server."
    />
  ) : (
    <Layout title="Locations">
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-display font-bold text-foreground">Locations</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Physical sites where stock is held. Pick a default location on each product (after the product name).
            </p>
          </div>
          {canCreate ? (
            <Button variant="premium" className="gap-2" onClick={startCreate}>
              <Plus className="w-4 h-4" /> Add location
            </Button>
          ) : null}
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search name, city, country, address…"
                className="glass-input pl-9 h-10"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
              <SheetTrigger asChild>
                <Button variant="glass" className="gap-2 shrink-0">
                  <Filter className="w-4 h-4" />
                  Filters
                  {appliedFiltersCount > 0 ? (
                    <span className="ml-0.5 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">{appliedFiltersCount}</span>
                  ) : null}
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[92vw] sm:w-[420px] overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>Filter locations</SheetTitle>
                  <SheetDescription>Refine by address, activity, linked products, and created date.</SheetDescription>
                </SheetHeader>
                <div className="mt-6 space-y-5">
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Name contains</Label>
                    <Input className="glass-input h-10" value={filterName} onChange={(e) => setFilterName(e.target.value)} autoComplete="off" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">City</Label>
                      <Input className="glass-input h-10" value={filterCity} onChange={(e) => setFilterCity(e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">State</Label>
                      <Input className="glass-input h-10" value={filterState} onChange={(e) => setFilterState(e.target.value)} />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Country</Label>
                    <Input className="glass-input h-10" value={filterCountry} onChange={(e) => setFilterCountry(e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Min. products using</Label>
                    <Input
                      className="glass-input h-10"
                      type="number"
                      min={0}
                      value={minProducts}
                      onChange={(e) => setMinProducts(e.target.value)}
                      placeholder="e.g. 1"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Created date</Label>
                    <Popover modal={true}>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          className={cn("glass-input h-10 w-full justify-start text-left font-normal", !filterCreatedDate && "text-muted-foreground")}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4 shrink-0 opacity-70" />
                          {filterCreatedDate ? format(filterCreatedDate, "PPP") : "Any date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="z-[100] w-auto p-0" align="start" sideOffset={6}>
                        <Calendar mode="single" selected={filterCreatedDate} onSelect={setFilterCreatedDate} initialFocus />
                        <div className="flex gap-2 border-t p-2">
                          <Button type="button" variant="ghost" size="sm" className="flex-1" onClick={() => setFilterCreatedDate(undefined)}>
                            Clear
                          </Button>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="grid grid-cols-1 gap-2">
                    <label className="flex items-center justify-between gap-3 rounded-lg border border-border/40 bg-muted/15 px-3 py-2 text-sm text-muted-foreground cursor-pointer">
                      <span>Active only</span>
                      <Checkbox
                        checked={onlyActive}
                        onCheckedChange={(checked) => {
                          const next = Boolean(checked);
                          setOnlyActive(next);
                          if (next) setOnlyInactive(false);
                        }}
                      />
                    </label>
                    <label className="flex items-center justify-between gap-3 rounded-lg border border-border/40 bg-muted/15 px-3 py-2 text-sm text-muted-foreground cursor-pointer">
                      <span>Inactive only</span>
                      <Checkbox
                        checked={onlyInactive}
                        onCheckedChange={(checked) => {
                          const next = Boolean(checked);
                          setOnlyInactive(next);
                          if (next) setOnlyActive(false);
                        }}
                      />
                    </label>
                  </div>
                </div>
                <SheetFooter className="mt-6 gap-2">
                  <Button type="button" variant="glass" className="w-full" onClick={resetSheetFilters}>
                    Reset
                  </Button>
                  <SheetClose asChild>
                    <Button type="button" variant="premium" className="w-full">
                      Apply filters
                    </Button>
                  </SheetClose>
                </SheetFooter>
              </SheetContent>
            </Sheet>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="overflow-x-auto flex-1 min-w-0 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background whitespace-nowrap">
                {(
                  [
                    { id: "today" as const, label: "Today", mobile: "Today" },
                    { id: "week" as const, label: "Week", mobile: "7D" },
                    { id: "month" as const, label: "Month", mobile: "30D" },
                    { id: "all" as const, label: "All", mobile: "All" },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setQuickDate(opt.id)}
                    className={cn(
                      "h-full px-3 text-sm transition-colors",
                      quickDate === opt.id ? "bg-primary/10 text-foreground font-medium" : "text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    <span className="sm:hidden">{opt.mobile}</span>
                    <span className="hidden sm:inline">{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
              <button
                type="button"
                onClick={() => setViewMode("grid")}
                className={cn("h-full px-3", viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50")}
                aria-label="Grid view"
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setViewMode("list")}
                className={cn("h-full px-3", viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50")}
                aria-label="List view"
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <GridPerPageSelect id="wh-page-size" pageSize={pageSize} onPageSizeChange={setPageSize} options={PAGE_SIZE_OPTIONS} />
            <div className="w-[190px] shrink-0">
              <Select value={sortBy} onValueChange={(v) => setSortBy(v as WhSortBy)}>
                <SelectTrigger className="glass-input h-10 w-full">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="latest">Newest first</SelectItem>
                  <SelectItem value="oldest">Oldest first</SelectItem>
                  <SelectItem value="name_asc">Name: A to Z</SelectItem>
                  <SelectItem value="name_desc">Name: Z to A</SelectItem>
                  <SelectItem value="city_asc">City: A to Z</SelectItem>
                  <SelectItem value="city_desc">City: Z to A</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {paginated.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border/60 bg-muted/10 p-10 text-center text-sm text-muted-foreground">
            {rows.length === 0 ? "No locations yet. Click “Add location” to create one." : "No locations match your filters."}
          </div>
        ) : viewMode === "grid" ? (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4"
          >
            {paginated.map((w, i) => {
              const usage = usageById.get(w.id) ?? 0;
              return (
                <motion.div
                  key={w.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.03 }}
                >
                  <CatalogEntityCard
                    title={w.name}
                    titleIcon={MapPin}
                    statusBadge={
                      w.isActive
                        ? { label: "Active", className: "bg-emerald-500/15 text-emerald-700" }
                        : { label: "Inactive", className: "bg-muted text-muted-foreground" }
                    }
                    primaryMeta={{
                      label: "Region",
                      value: locationLine(w),
                      icon: MapPin,
                      iconClassName: "text-sky-600",
                    }}
                    secondaryMeta={{
                      label: "Products",
                      value: String(usage),
                      icon: Package,
                      iconClassName: "text-amber-600",
                    }}
                    footer={`Created: ${formatCatalogAuditDate(w.createdAt)}`}
                    onView={() => setViewRow(w)}
                    onEdit={canUpdate ? () => startEdit(w) : undefined}
                    onDelete={canDelete ? () => requestRemove(w) : undefined}
                  />
                </motion.div>
              );
            })}
          </motion.div>
        ) : (
          <div className="rounded-xl border border-border/50 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 border-b border-border/50">
                <tr>
                  <th className="text-left p-3 font-medium">Name</th>
                  <th className="text-left p-3 font-medium hidden md:table-cell">Location</th>
                  <th className="text-left p-3 font-medium">Status</th>
                  <th className="text-right p-3 font-medium">Products</th>
                  <th className="text-right p-3 font-medium w-32">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((w) => {
                  const usage = usageById.get(w.id) ?? 0;
                  return (
                    <tr key={w.id} className="border-b border-border/40 last:border-0 hover:bg-muted/20">
                      <td className="p-3 font-medium text-foreground">{w.name}</td>
                      <td className="p-3 text-muted-foreground hidden md:table-cell">{locationLine(w)}</td>
                      <td className="p-3">{w.isActive ? <span className="text-emerald-600">Active</span> : <span className="text-muted-foreground">Inactive</span>}</td>
                      <td className="p-3 text-right tabular-nums">{usage}</td>
                      <td className="p-3 text-right">
                        <div className="inline-flex gap-0.5 justify-end">
                          <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => setViewRow(w)}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          {canUpdate ? (
                            <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => startEdit(w)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                          ) : null}
                          {canDelete ? (
                            <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => requestRemove(w)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <GridPaginationFooter page={page} totalPages={totalPages} totalCount={totalCount} rangeStart={rangeStart} rangeEnd={rangeEnd} onPageChange={setPage} />

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>{editing ? "Edit location" : "Add location"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input className="glass-input h-10" value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Country</Label>
                  <Input className="glass-input h-10" value={country} onChange={(e) => setCountry(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>State</Label>
                  <Input className="glass-input h-10" value={state} onChange={(e) => setState(e.target.value)} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>City</Label>
                <Input className="glass-input h-10" value={city} onChange={(e) => setCity(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Address</Label>
                <Textarea className="glass-input min-h-[80px]" value={address} onChange={(e) => setAddress(e.target.value)} />
              </div>
              <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
                <Checkbox checked={isActive} onCheckedChange={(c) => setIsActive(Boolean(c))} />
                Active
              </label>
            </div>
            <DialogFooter>
              <Button variant="glass" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button variant="premium" disabled={isSaving} onClick={() => void save()}>
                {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={Boolean(viewRow)} onOpenChange={(o) => !o && setViewRow(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-primary" />
                {viewRow?.name}
              </DialogTitle>
            </DialogHeader>
            {viewRow ? (
              <div className="space-y-2 text-sm">
                <p className="text-muted-foreground flex items-start gap-2">
                  <MapPin className="h-4 w-4 shrink-0 mt-0.5" />
                  <span>{locationLine(viewRow)}</span>
                </p>
                {viewRow.address.trim() ? <p className="text-foreground/90 whitespace-pre-wrap">{viewRow.address}</p> : null}
                <p className="text-xs text-muted-foreground pt-2 border-t border-border/50">
                  {viewRow.isActive ? "Active" : "Inactive"} · {formatCatalogAuditDate(viewRow.createdAt)}
                </p>
              </div>
            ) : null}
            <DialogFooter>
              <Button variant="premium" onClick={() => setViewRow(null)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <ConfirmAlertDialog
          open={Boolean(deleteTarget)}
          onOpenChange={(o) => !o && setDeleteTarget(null)}
          title="Delete location?"
          description={deleteTarget ? `Remove “${deleteTarget.name}”? This cannot be undone.` : ""}
          confirmLabel="Delete"
          destructive
          loading={isDeleting}
          onConfirm={() => void confirmRemove()}
        />

        {infoDialog ? (
          <Dialog open onOpenChange={() => setInfoDialog(null)}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{infoDialog.title}</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-muted-foreground">{infoDialog.description}</p>
              <DialogFooter>
                <Button variant="premium" onClick={() => setInfoDialog(null)}>
                  OK
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        ) : null}
      </div>
    </Layout>
  );
};

export default Warehouses;
